import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { addPets } from '../../redux/actions/pets';
import { getAllSpecies } from '../../redux/actions/species';
import { getAllBreeds } from '../../redux/actions/breeds';
import { getAllClients } from '../../redux/actions/clients';
import { getAllProducts } from '../../redux/actions/products';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import ModalAddSpecie from '../../components/ModalAddSpecie';
import ModalAddBreed from '../../components/ModalAddBreed';
import ModalAddClient from '../../components/ModalAddClient';
import ModalAddProduct from '../../components/ModalAddProduct';
/* internal component */
import Responsibles from './Responsibles';
/* form-validation */
import schema from '../../form-validation/pet';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import ptBR from 'date-fns/locale/pt-BR';
import config from '../../api/config.json';
import handleChangeCurrency from '../../utils/handleChangeCurrency';
registerLocale('ptBR', ptBR);

function Add (props) {
  const { handleModalClose } = props;
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setError, setValue } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const pets = useSelector(state => state.pets);
  const INITIAL_VALUES = {
    name: '',
    color: '',
    specie: '',
    breed: '',
    born: new Date(),
    gender: '',
    size: '',
    microchip: '',
    spayed: false,
    pedigree: '',
    pedigreeMother: '',
    pedigreeFather: '',
    responsibles: [],
    additionalInformation: '',
    image: '',
    weight: '0,00',
    food: '',
    company
  };
  const [data, setData] = useState(INITIAL_VALUES);
  const species = useSelector(state => state.species.data);
  const breeds = useSelector(state => state.breeds.data);
  const clients = useSelector(state => state.clients.data);
  const products = useSelector(state => state.products.data);
  const [specieOptions, setSpecieOptions] = useState([firstOption]);
  const [breedOptions, setBreedOptions] = useState([firstOption]);
  const [productOptions, setProductOptions] = useState([firstOption]);
  const [showModalAddSpecie, setShowModalAddSpecie] = useState(false);
  const [showModalAddBreed, setShowModalAddBreed] = useState(false);
  const [showModalAddClient, setShowModalAddClient] = useState(false);
  const [showModalAddProduct, setShowModalAddProduct] = useState(false);

  useEffect(() => {
    dispatch(getAllSpecies({ company }));
    dispatch(getAllBreeds({ company }));
    dispatch(getAllClients({ company }));
    dispatch(getAllProducts({ company }));
  }, [dispatch]);

  useEffect(() => {
    if (species.length) {
      const options = species
        .map(specie => ({
          value: specie._id,
          label: `${specie.name}`,
          sortValue: `${specie.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setSpecieOptions([...options]);
    }
  }, [species, setSpecieOptions]);

  useEffect(() => {
    if (breeds.length) {
      const options = breeds
        .map(breed => ({
          value: breed._id,
          specie: breed.specie._id,
          label: `${breed.name}`,
          sortValue: `${breed.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setBreedOptions([...options]);
    }
  }, [breeds, setBreedOptions]);

  useEffect(() => {
    if (products.length) {
      const options = products
        .map(product => ({
          value: product._id,
          label: `${product.name} | ${product.barCode}`,
          sortValue: `${product.name} | ${product.barCode}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setProductOptions([...options]);
    }
  }, [products, setProductOptions]);

  const handleChangeImage = evt => {
    const { files } = evt.target;
    const suportedTypes = ['image/png', 'image/jpeg'];
    files[0] && suportedTypes.includes(files[0].type)
      ? setData({ ...data, image: files[0] })
      : setData({ ...data, image: '' });
  };

  const submit = () => {
    const resetForm = () => {
      reset();
      setData({ ...INITIAL_VALUES });
    };
    if (data.responsibles.length) {
      const newData = {
        ...data,
        weight: parseFloat(data.weight.replace(/,/g, '.'))
      };
      dispatch(addPets(newData, resetForm, submitButton.current));
    }
  };

  return (
    <div className="container-fluid">
      <ModalAddSpecie
        showModal={showModalAddSpecie}
        handleModalClose={() => setShowModalAddSpecie(false)}
      />
      <ModalAddBreed
        showModal={showModalAddBreed}
        handleModalClose={() => setShowModalAddBreed(false)}
      />
      <ModalAddClient
        showModal={showModalAddClient}
        handleModalClose={() => setShowModalAddClient(false)}
      />
      <ModalAddProduct
        showModal={showModalAddProduct}
        handleModalClose={() => setShowModalAddProduct(false)}
      />
      {pets.addSuccess && (
        <AlertMsg type="success" msg="Pet cadastrado com sucesso" />
      )}
      {pets.errorContent &&
        !pets.errorContent.message.includes('400') &&
        pets.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro do pet" />
      )}
      {pets.errorContent &&
        pets.errorContent.message.includes('400') &&
        pets.addError && (
        <AlertMsg
          type="danger"
          msg="Um pet com o chip informado já está cadastrado"
        />
      )}
      {!pets.addSuccess && !pets.addError && (
        <AlertMsg type="info" msg="Área cadastro de pets" />
      )}
      <FormHeader title="Cadastrar Pet" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group text-center">
              <img
                className="p-2"
                width="200"
                height="200"
                src={
                  data.image
                    ? URL.createObjectURL(data.image)
                    : `${config.protocol}://${config.ipServerGW}:${config.portServerGW}/api/pets/image/5f45740fa04a088351d96383`
                }
                alt="Avatar"
              />
              <input
                type="file"
                id="image"
                name="image"
                className="form-control-file"
                accept="image/png,image/jpeg"
                onChange={handleChangeImage}
              />
            </div>
          </div>
          <div className="col-sm-8">
            <div className="row">
              <div className="col-sm-8">
                <div className="form-group">
                  <label htmlFor="name">
                    Nome<span className="field-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    autoComplete="off"
                    value={data.name}
                    onChange={evt => handleChangeDefault(evt, data, setData)}
                    ref={register}
                  />
                  {errors.name && errors.name.type === 'required' && (
                    <ErrorMsgField type="required" />
                  )}
                </div>
              </div>
              <div className="col-sm-4">
                <div className="form-group">
                  <label htmlFor="color">
                    Cor<span className="field-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control text-lowercase"
                    id="color"
                    name="color"
                    autoComplete="off"
                    value={data.color}
                    onChange={evt => handleChangeDefault(evt, data, setData)}
                    ref={register}
                  />
                  {errors.color && errors.color.type === 'required' && (
                    <ErrorMsgField type="required" />
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="specie">
                  Espécie
                  <span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="specie"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'specie' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={specieOptions[0]}
                  value={specieOptions.find(
                    specie => specie.value === data.specie
                  )}
                  options={specieOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Espécie"
                    disabled={showModalAddSpecie}
                    onClick={() => setShowModalAddSpecie(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Espécie
                  </button>
                </div>
              </div>
              {errors.specie && errors.specie.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="specie">
                  Raça
                  <span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="breed"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'breed' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={breedOptions[0]}
                  value={breedOptions.find(breed => breed.value === data.breed)}
                  options={breedOptions.filter(
                    breed => breed.specie === data.specie || breed.value === ''
                  )}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                  isDisabled={!data.specie}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Raça"
                    disabled={showModalAddBreed}
                    onClick={() => setShowModalAddBreed(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Raça
                  </button>
                </div>
              </div>
              {errors.breed && errors.breed.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label>
                Data de Nascimento<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="born"
                onChange={date => setData({ ...data, born: date })}
                selected={data.born}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.born} />}
              />
              {errors.born && errors.born.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="gender">
                Gênero<span className="field-required">*</span>
              </label>
              <select
                id="gender"
                name="gender"
                className="form-control"
                value={data.gender}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
              </select>
              {errors.gender && errors.gender.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="size">
                Porte<span className="field-required">*</span>
              </label>
              <select
                id="size"
                name="size"
                className="form-control"
                value={data.size}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                <option value="S">Pequeno</option>
                <option value="M">Médio</option>
                <option value="L">Grande</option>
              </select>
              {errors.size && errors.size.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-8">
            <div className="form-group">
              <label htmlFor="microchip">Microchip</label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="microchip"
                name="microchip"
                autoComplete="off"
                value={data.microchip}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="spayed">Animal Castrado</label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="spayed"
                  checked={data.spayed}
                  onChange={evt =>
                    setData({ ...data, [evt.target.name]: evt.target.checked })
                  }
                >
                  {data.spayed ? 'Sim' : 'Não'}
                </Switch>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="weight">
                Kg Peso<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="weight"
                name="weight"
                number="on"
                value={data.weight}
                autoComplete="off"
                placeholder="Formato permitido valor com duas casas decimais 0,00"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.weight && errors.weight.type === 'currency' && (
                <ErrorMsgField
                  type="currency"
                  msg="Peso com formato inválido"
                />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="food">
                  Ração
                </label>
                <Select
                  name="food"
                  className="col p-0"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'food' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={productOptions[0]}
                  value={productOptions.find(
                    product => product.value === data.food
                  )}
                  options={productOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Produto"
                    disabled={showModalAddProduct}
                    onClick={() => setShowModalAddProduct(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Produto
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="pedigree">Pedigree</label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="pedigree"
                name="pedigree"
                autoComplete="off"
                value={data.pedigree}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="pedigreeFather">Pedigree do Pai</label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="pedigreeFather"
                name="pedigreeFather"
                autoComplete="off"
                value={data.pedigreeFather}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="pedigreeMother">Pedigree da Mãe</label>
              <input
                type="text"
                className="form-control text-lowercase"
                id="pedigreeMother"
                name="pedigreeMother"
                autoComplete="off"
                value={data.pedigreeMother}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-success"
            title="Adicionar Cliente"
            disabled={showModalAddClient}
            onClick={() => setShowModalAddClient(true)}
          >
            <i className="fa fa-plus" />
            &nbsp;Cliente
          </button>
        </div>
        <Responsibles clients={clients} data={data} setData={setData} />
        <div className="form-group">
          <label htmlFor="additionalInformation">Informações Adicionais</label>
          <textarea
            className="form-control"
            id="additionalInformation"
            name="additionalInformation"
            rows="3"
            value={data.additionalInformation}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
        </div>
        {handleModalClose ? (
          <div className="container-fluid p-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fa fa-times"></i>
              &nbsp;Fechar
            </button>
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              <i className="fa fa-save"></i>
              &nbsp;Salvar
            </button>
          </div>
        ) : (
          <FormFooter url="/pets" reference={submitButton} />
        )}
      </form>
    </div>
  );
}

Add.propTypes = {
  handleModalClose: PropTypes.func
};

export default Add;
